import React, { useState, useEffect } from 'react';
import { Loader, Info, ArrowDown } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveJob, saveMultipleJobs, selectJobStrategy } from '../../../redux/SavedJobSlice/savedJobSlice';
import { toast } from 'react-toastify';
import api from '../../../api/axios';
import { STRATEGY_CATALOG } from '../../../constants/strategyCatalog';
import { recommendStrategy } from '../../../utils/strategyRecommendation';
import { clientDataCache, getCurrentUserIdFromStorage } from '../../../utils/clientDataCache';

const JobAcquisitionPlan = ({ job, companyName, isOnboardingPreview = false, onPreviewActivate = null }) => {
    const [expandedStrategy, setExpandedStrategy] = useState(null);
    const [selectingKey, setSelectingKey] = useState(null);
    const [aiAdvice, setAiAdvice] = useState(null);
    const [loadingAdvice, setLoadingAdvice] = useState(false);
    const [selectedSubStrategyKey, setSelectedSubStrategyKey] = useState('');
    const [activationError, setActivationError] = useState('');
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(state => state.auth.user);

    // --- AI STRATEGY ADVICE FETCHING ---
    useEffect(() => {
        const fetchAdvice = async () => {
            if (!user || !job) return;

            const resolvedUserId = user?._id || user?.id || getCurrentUserIdFromStorage();
            const resolvedJobId =
                job?._id ||
                job?.id ||
                job?.savedJobId ||
                job?.jobId?._id ||
                (typeof job?.jobId === 'string' ? job.jobId : null);
            const cacheKey = `${resolvedJobId || companyName || job?.companyName || 'unknown'}`;

            if (resolvedUserId) {
                const cached = clientDataCache.readStrategyAdvice(resolvedUserId, cacheKey);
                if (cached?.reason) {
                    setAiAdvice(cached);
                    return;
                }
            }

            setLoadingAdvice(true);
            try {
                const token = localStorage.getItem("token");
                const res = await api.post('/api/deepseek/strategy-advice', 
                    { 
                        jobDetails: {
                            jobTitle: job.jobPosition || job.jobTitle,
                            companyName: companyName || job.companyName,
                            jobDescription: job.jobDescription || job.description,
                            jobLocation: job.jobLocation || job.location || '',
                            department: job.department || ''
                        },
                        userProfile: {
                            name: user?.name || '',
                            jobTitle: user?.jobTitle || [],
                            currentTitle: user?.currentTitle || '',
                            skills: user?.skills || [],
                            experience: user?.experience || [],
                            preferredIndustry: user?.preferredIndustry || [],
                            preferredDepartment: user?.preferredDepartment || [],
                            careerGoal: user?.careerGoal || null,
                            experienceYears: user?.experienceYears || 0,
                            signalProfile: user?.signalProfile || null
                        }
                    },
                    { headers: { "auth-token": token } }
                );
                
                if (res.data.success) {
                    setAiAdvice(res.data.advice);
                    if (resolvedUserId) {
                        clientDataCache.writeStrategyAdvice(resolvedUserId, cacheKey, res.data.advice);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch AI strategy advice:", err);
                // Fallback to null - static logic will handle it
            } finally {
                setLoadingAdvice(false);
            }
        };

        fetchAdvice();
    }, [job, user, companyName]);

    const handleSelectStrategy = async (strategyId, subStrategyKey) => {
        if (!user || !job) {
            toast.error("Please log in to select a strategy.");
            return;
        }

        // Onboarding tour uses a demo job id — API persistence / redirect would fail or exit the wizard.
        if (isOnboardingPreview && typeof onPreviewActivate === 'function') {
            setSelectingKey(subStrategyKey || '');
            try {
                toast.success('Plan selected — continuing the tour.');
                onPreviewActivate();
            } finally {
                setSelectingKey(null);
            }
            return;
        }

        setActivationError('');
        setSelectingKey(subStrategyKey || '');
        
        try {
            const selectedUserId = user?._id || user?.id;
            const baseJobId =
                job?.jobId?._id ||
                (typeof job?.jobId === 'string' ? job.jobId : null) ||
                job?.id ||
                job?._id;

            if (!selectedUserId) {
                const msg = 'Unable to activate strategy: missing user id.';
                setActivationError(msg);
                toast.error(msg);
                return;
            }

            if (!baseJobId) {
                const msg = 'Unable to activate strategy: missing job id.';
                setActivationError(msg);
                toast.error(msg);
                return;
            }

            // If this modal was opened from a Hiring Signal (not an actual Job record), the id may not map to Jobs collection.
            // SavedJobs.saveJob requires an existing Jobs document, so activation cannot proceed.
            const isObjectIdLike = typeof baseJobId === 'string' ? /^[a-fA-F0-9]{24}$/.test(baseJobId.trim()) : true;
            if (!isObjectIdLike) {
                const msg = 'Unable to activate strategy from this view. Save the job first, then activate the plan.';
                setActivationError(msg);
                toast.error(msg);
                return;
            }

            const jobAgeHours = Math.max(jobAge.hours || 0, 0);
            const hasOpenRole = Boolean(job?.jobPosition || job?.jobTitle || job?.jobId);

            const selectionPayload = {
                userId: selectedUserId,
                jobId: baseJobId,
                strategyId,
                subStrategyKey,
                contextSnapshot: {
                    jobAgeHours,
                    hasOpenRole,
                    companyName: companyName || job.companyName || '',
                    subStrategyKey,
                    selectedAt: new Date().toISOString()
                }
            };

            try {
                await dispatch(selectJobStrategy(selectionPayload)).unwrap();
            } catch (selectionErr) {
                const rawMessage =
                    (typeof selectionErr === 'string' && selectionErr) ||
                    selectionErr?.message ||
                    selectionErr?.error ||
                    '';
                const message = rawMessage.toLowerCase();
                if ((!message.includes('not found') && !message.includes('404')) || !baseJobId) {
                    throw selectionErr;
                }

                // If this came from Hiring Signals, baseJobId may not exist in the Jobs collection.
                // Create a lightweight Job from the signal first, then save+activate against that real Job id.
                let resolvedJobId = baseJobId;
                try {
                    await dispatch(saveJob({ userId: selectedUserId, jobId: resolvedJobId })).unwrap();
                } catch (saveErr) {
                    const saveMsg = String(saveErr?.message || saveErr || '').toLowerCase();
                    if (!saveMsg.includes('job not found')) {
                        throw saveErr;
                    }

                    const companyId =
                        job?.companyId ||
                        job?.company?._id ||
                        job?.company ||
                        null;

                    const derivedCompanyName = companyName || job?.companyName || job?.company?.name || 'Company';

                    if (!companyId) {
                        throw new Error('Unable to save: missing company id for this signal.');
                    }

                    const created = await api.post('/api/jobs/create-from-signal', {
                        hsignalId: resolvedJobId,
                        companyId,
                        organizationId: job?.organizationId || job?.company?.organizationId,
                        companyName: derivedCompanyName,
                        companyLogo: job?.companyLogo,
                        jobPosition: job?.jobPosition || job?.jobTitle,
                        jobLocation: job?.jobLocation,
                        jobDesc: job?.jobDesc,
                        department: job?.department
                    });

                    const newJobId = created?.data?.jobId;
                    if (!newJobId) {
                        throw new Error('Failed to create a job record for this signal.');
                    }

                    resolvedJobId = newJobId;

                    await dispatch(saveJob({ userId: selectedUserId, jobId: resolvedJobId })).unwrap();
                }

                await dispatch(selectJobStrategy({ ...selectionPayload, jobId: resolvedJobId })).unwrap();
            }
            
            toast.success("Strategy activated! Routing you to your 14-Day Sprint...");

            navigate('/dailytasks');
            setTimeout(() => {
                window.location.assign('/dailytasks');
            }, 120);
        } catch (error) {
            console.error("Failed to select strategy:", error);
            const message =
                (typeof error === 'string' && error) ||
                error?.message ||
                error?.error ||
                'Failed to activate strategy.';
            setActivationError(message);
            toast.error(message);
        } finally {
            setSelectingKey(null);
        }
    };

    // Calculate job age
    const getJobAge = () => {
        if (!job?.datentime && !job?.postedAt) {
            return { display: 'recently', hours: 48 };
        }

        const postedDate = new Date(job.datentime || job.postedAt);
        const now = new Date();
        const diffMs = now - postedDate;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMinutes < 60) {
            return { display: `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`, hours: diffHours };
        } else if (diffHours < 24) {
            return { display: `${diffHours} hour${diffHours !== 1 ? 's' : ''}`, hours: diffHours };
        } else {
            return { display: `${diffDays} day${diffDays !== 1 ? 's' : ''}`, hours: diffHours };
        }
    };

    const jobAge = getJobAge();

    // Determine recommended strategy - Use AI if available, otherwise deterministic fallback
    const getRecommendedStrategyId = () => {
        if (aiAdvice?.strategyId) return aiAdvice.strategyId;

        return recommendStrategy({ job, userProfile: user || {} }).strategyId;
    };

    const recommendedId = getRecommendedStrategyId();

    const strategies = STRATEGY_CATALOG;
    const recommendedStrategy = strategies.find((strategy) => strategy.id === recommendedId) || strategies[0];
    const subStrategies = recommendedStrategy?.subStrategies || [];

    const getRecommendedSubStrategyKey = () => {
        const subStrategies = recommendedStrategy?.subStrategies || [];
        if (!subStrategies.length) return '';

        if (aiAdvice?.subStrategyKey) {
            const hasAiSuggested = subStrategies.some((item) => item.key === aiAdvice.subStrategyKey);
            if (hasAiSuggested) return aiAdvice.subStrategyKey;
        }

        if (recommendedStrategy.id === 4) {
            return jobAge.hours <= 24 ? 'same-day-strike' : '24h-escalation';
        }

        if (recommendedStrategy.id === 2) {
            return jobAge.hours <= 48 ? 'signal-boosted-outreach' : 'apply-then-message';
        }

        if (recommendedStrategy.id === 3) {
            return jobAge.hours <= 72 ? 'advocate-seeding' : 'referral-with-proof';
        }

        return subStrategies[0].key;
    };

    const recommendedSubStrategyKey = getRecommendedSubStrategyKey();

    const persistedStrategyId = Number(job?.activeStrategy || 0);
    const persistedSubStrategyKey =
        job?.activeSubStrategyKey ||
        job?.strategyContextSnapshot?.subStrategyKey ||
        '';

    const toggleStrategy = (id) => {
        setExpandedStrategy(expandedStrategy === id ? null : id);
    };

    return (
        <div className="p-6 max-w-5xl mx-auto font-dmsans">
            {/* AI SMART PICK SECTION */}
            <div className={`mb-8 p-6 rounded-xl border transition-all duration-300 ${
                loadingAdvice 
                    ? 'border-slate-200 bg-slate-50' 
                    : 'border-slate-200 bg-white shadow-sm'
                }`}
            >
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 text-left">

                        {loadingAdvice ? (
                            <div className="space-y-3 animate-pulse">
                                <div className="h-6 w-3/4 bg-slate-200 rounded-lg" />
                                <div className="h-4 w-full bg-slate-200 rounded-lg" />
                                <div className="h-4 w-5/6 bg-slate-200 rounded-lg" />
                            </div>
                        ) : aiAdvice ? (
                            <>
                                <h3 className="text-xl font-semibold text-slate-900 mb-2 text-left">
                                    Why this strategy path is your best move
                                </h3>
                                <p className="text-slate-600 text-sm leading-relaxed mb-1 border-l-2 border-slate-300 pl-4 py-1 text-left">
                                    "{aiAdvice.reason}"
                                </p>
                            </>
                        ) : (
                            <>
                                <h3 className="text-xl font-semibold text-slate-900 mb-2 text-left">
                                    Recommended strategy path
                                </h3>
                                <p className="text-slate-600 text-sm leading-relaxed text-left">
                                    Based on the job's age ({jobAge.display} ago), we picked the strongest strategy to improve your response odds.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                     <h2 className="text-2xl font-semibold text-gray-900 text-left">
                        Choose your plan
                    </h2>
                    <p className="text-gray-500 text-sm text-left">Select the blueprint for your networking sprint.</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-lg text-xs font-bold text-slate-600 border border-slate-200">
                    <Info size={14} />
                    Posted {jobAge.display} ago
                </div>
            </div>

            {activationError && (
                <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                    {activationError}
                </div>
            )}

            {/* Sub-Strategy Cards (Matched Category Only) */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                {subStrategies.map((subStrategy, index) => {
                    const IconComponent = recommendedStrategy.icon;
                    const isBestMatch = subStrategy.key === recommendedSubStrategyKey;
                    const isExpanded = expandedStrategy === subStrategy.key;
                    const isManuallySelected = selectedSubStrategyKey === subStrategy.key;
                    const isPersistedSelected =
                        persistedStrategyId === Number(recommendedStrategy.id) &&
                        persistedSubStrategyKey === subStrategy.key;
                    const isSelected = isManuallySelected || isPersistedSelected;
                    const isSelecting = selectingKey === subStrategy.key;

                    return (
                        <React.Fragment key={subStrategy.key || index}>
                            {index > 0 && (
                                <div className="flex flex-col items-center justify-center -my-2 opacity-90 z-10">
                                    <div className="h-3 border-l-2 border-dashed border-slate-300"></div>
                                    <div className="flex items-center gap-2 bg-slate-50 px-4 py-1.5 rounded-full border border-slate-200 text-xs font-semibold text-slate-600 shadow-sm my-1">
                                        <ArrowDown size={14} className="text-slate-400" />
                                        If no response, seamlessly transition to
                                        <ArrowDown size={14} className="text-slate-400" />
                                    </div>
                                    <div className="h-3 border-l-2 border-dashed border-slate-300"></div>
                                </div>
                            )}
                            <div
                                className={`group relative rounded-2xl transition-all duration-300 border ${
                                    isBestMatch || isSelected ? 'border-2 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                                }`}
                                style={{
                                    borderColor: isBestMatch || isSelected ? recommendedStrategy.color : undefined,
                                    background: 'white'
                                }}
                            >
                                <div className="p-6 h-full flex flex-col">
                                    <div className="flex items-start gap-4 mb-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex-shrink-0 flex items-center justify-center"
                                            style={{ backgroundColor: `${recommendedStrategy.color}15` }}
                                        >
                                            <IconComponent size={28} style={{ color: recommendedStrategy.color }} />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
                                                    {subStrategy.name}
                                                </h3>
                                                {isBestMatch && (
                                                    <div
                                                        className="px-2 py-1 text-[10px] font-semibold rounded-full uppercase tracking-wide border"
                                                        style={{ color: recommendedStrategy.color, borderColor: `${recommendedStrategy.color}55`, backgroundColor: `${recommendedStrategy.color}10` }}
                                                    >
                                                        Best match
                                                    </div>
                                                )}
                                            </div>

                                            <p className="text-gray-500 font-medium text-sm leading-relaxed text-left">
                                                {subStrategy.whenToUse}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-3">Tasks</p>
                                        <div className="space-y-2">
                                            {(isExpanded ? subStrategy.actions : (subStrategy.actions || []).slice(0, 3)).map((action, actionIdx) => (
                                                <div key={actionIdx} className="flex items-start gap-3 p-2 bg-white border border-gray-100 rounded-xl">
                                                    <span className="flex-shrink-0 w-5 h-5 rounded-md bg-gray-50 flex items-center justify-center text-[10px] font-semibold text-gray-500 border border-gray-100">
                                                        {actionIdx + 1}
                                                    </span>
                                                    <span className="text-xs text-gray-700 leading-snug text-left">{action}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mt-auto flex flex-col sm:flex-row items-center gap-3 border-t border-gray-100 pt-4">
                                        <button
                                            onClick={() => {
                                                setSelectedSubStrategyKey(subStrategy.key);
                                                handleSelectStrategy(recommendedStrategy.id, subStrategy.key);
                                            }}
                                            disabled={Boolean(selectingKey)}
                                            className={`flex-1 w-full px-6 py-3 rounded-xl font-semibold text-xs text-white transition-all flex items-center justify-center gap-2 active:scale-95 ${
                                                isSelecting
                                                    ? 'opacity-70 cursor-wait'
                                                    : 'hover:opacity-90'
                                            }`}
                                            style={{
                                                backgroundColor: recommendedStrategy.color,
                                                boxShadow: 'none'
                                            }}
                                        >
                                                {isSelecting ? (
                                                <>
                                                    <Loader className="animate-spin" size={16} />
                                                    Activating...
                                                </>
                                            ) : (
                                                    isPersistedSelected ? 'Activated' : 'Activate Strategy'
                                            )}
                                        </button>

                                        {(subStrategy.actions || []).length > 3 && (
                                            <button
                                                onClick={() => toggleStrategy(subStrategy.key)}
                                                className="w-full sm:w-auto px-4 py-3 rounded-xl font-bold text-xs text-slate-500 hover:bg-slate-50 transition-all border border-transparent hover:border-slate-200"
                                            >
                                                {isExpanded ? 'Hide Tasks' : 'View All Tasks'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

        </div>
    );
};

export default JobAcquisitionPlan;
